export const DEFAULT_PLOT_WIDTH_PX = 880;
export const LABEL_ROTATION_DEG = -32;
const LABEL_FONT_SIZE = 11;
const LABEL_PADDING_PX = 6;

export const formatSwingTickDate = (value, spanDays, { compact = false } = {}) => {
  const date = new Date(value);
  const yearSuffix = String(date.getFullYear()).slice(-2);

  if (compact || spanDays <= 900) {
    return `${date.getMonth() + 1}/${date.getDate()}/${yearSuffix}`;
  }

  return String(date.getFullYear());
};

export const estimateLabelFootprintPx = (label, fontSize = LABEL_FONT_SIZE) => {
  const charWidth = fontSize * 0.57;
  const width = label.length * charWidth;
  const height = fontSize + 2;
  const angleRad = (Math.abs(LABEL_ROTATION_DEG) * Math.PI) / 180;

  return width * Math.cos(angleRad) + height * Math.sin(angleRad) + LABEL_PADDING_PX;
};

export const tradeDateToAnchorX = (tradeDate, chartPoints, plotWidthPx = DEFAULT_PLOT_WIDTH_PX) => {
  if (!chartPoints?.length) return 0;

  const count = chartPoints.length;
  if (count === 1) return plotWidthPx / 2;

  const target = Date.parse(tradeDate);
  const exactIndex = chartPoints.findIndex((point) => point.trade_date === tradeDate);
  if (exactIndex >= 0) {
    return (exactIndex / (count - 1)) * plotWidthPx;
  }

  const timestamps = chartPoints.map((point) => Date.parse(point.trade_date));
  if (target <= timestamps[0]) return 0;
  if (target >= timestamps[count - 1]) return plotWidthPx;

  for (let index = 0; index < count - 1; index += 1) {
    const start = timestamps[index];
    const end = timestamps[index + 1];
    if (target < start || target > end) continue;

    const ratio = (target - start) / Math.max(1, end - start);
    return ((index + ratio) / (count - 1)) * plotWidthPx;
  }

  return plotWidthPx;
};

export const buildLabelLayout = (
  tradeDate,
  chartPoints,
  spanDays,
  plotWidthPx = DEFAULT_PLOT_WIDTH_PX,
) => {
  const label = formatSwingTickDate(tradeDate, spanDays, { compact: true });
  const x = tradeDateToAnchorX(tradeDate, chartPoints, plotWidthPx);
  const footprint = estimateLabelFootprintPx(label);

  return {
    tradeDate,
    label,
    x,
    footprint,
    left: x - footprint,
    right: x + LABEL_PADDING_PX,
  };
};

export const labelsWouldOverlap = (layoutA, layoutB) => {
  const gap = Math.abs(layoutA.x - layoutB.x);
  const requiredGap = (layoutA.footprint + layoutB.footprint) / 2;
  return gap < requiredGap;
};

export const resolveReadableAxisTicks = ({
  candidateDates = [],
  chartPoints = [],
  spanDays = 0,
  getScore = () => 0,
  plotWidthPx = DEFAULT_PLOT_WIDTH_PX,
}) => {
  const uniqueDates = [...new Set(candidateDates.filter(Boolean))];
  if (!uniqueDates.length || !chartPoints?.length) return [];

  const ranked = uniqueDates
    .map((tradeDate) => ({
      tradeDate,
      score: getScore(tradeDate),
      layout: buildLabelLayout(tradeDate, chartPoints, spanDays, plotWidthPx),
    }))
    .sort((a, b) => b.score - a.score || a.layout.x - b.layout.x);

  const placed = [];
  ranked.forEach((candidate) => {
    const hasOverlap = placed.some((item) => labelsWouldOverlap(candidate.layout, item.layout));
    if (!hasOverlap) {
      placed.push(candidate);
    }
  });

  return placed
    .map((item) => item.tradeDate)
    .sort((a, b) => new Date(a) - new Date(b));
};

export const findLabelOverlaps = (
  ticks,
  chartPoints,
  spanDays,
  plotWidthPx = DEFAULT_PLOT_WIDTH_PX,
) => {
  const layouts = ticks.map((tradeDate) => buildLabelLayout(
    tradeDate,
    chartPoints,
    spanDays,
    plotWidthPx,
  ));

  const overlaps = [];
  for (let left = 0; left < layouts.length; left += 1) {
    for (let right = left + 1; right < layouts.length; right += 1) {
      if (labelsWouldOverlap(layouts[left], layouts[right])) {
        overlaps.push({
          left: layouts[left].tradeDate,
          right: layouts[right].tradeDate,
          gapPx: Math.abs(layouts[left].x - layouts[right].x),
          requiredGapPx: (layouts[left].footprint + layouts[right].footprint) / 2,
        });
      }
    }
  }

  return overlaps;
};

export const assertNoLabelOverlaps = (
  ticks,
  chartPoints,
  spanDays,
  plotWidthPx = DEFAULT_PLOT_WIDTH_PX,
) => {
  const overlaps = findLabelOverlaps(ticks, chartPoints, spanDays, plotWidthPx);
  if (!overlaps.length) return [];

  const details = overlaps
    .map((item) => `${item.left} ↔ ${item.right} (gap ${item.gapPx.toFixed(1)}px < ${item.requiredGapPx.toFixed(1)}px)`)
    .join('; ');

  throw new Error(`Overlapping axis labels detected: ${details}`);
};
